import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import { colors } from '../../design-system/colors';

export const FooterContainer = styled(motion.footer)`
  background: ${(props) =>
    props.$isHomePage
      ? colors.porcelain
      : `linear-gradient(135deg, ${colors.deepCharcoal} 0%, ${colors.deepCharcoal} 100%)`};
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  padding: 46px 40px 40px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${(props) =>
          props.$isHomePage
            ? 'rgba(0, 0, 0, 0.1)'
            : 'rgba(240, 244, 239, 0.15)'}
        50%,
      transparent 100%
    );
  }

  @media (max-width: 768px) {
    padding: 40px 20px 32px;
  }
`;

export const Container = styled.div`
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

export const TopSection = styled(motion.div)`
  display: grid;
  grid-template-columns: 1.5fr 0.8fr 1.2fr 1.2fr 1.5fr;
  gap: 40px;
  margin-bottom: 80px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    margin-bottom: 60px;
  }
`;

export const BrandColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

export const Logo = styled(motion.div)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 44px;
  margin-bottom: 24px;
  position: relative;
  transition: all 0.3s ease;
`;

export const LogoImage = styled.img`
  height: 100%;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  object-position: left center;
  display: block;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  filter: ${(props) =>
    props.$isHomePage ? 'none' : 'brightness(0) invert(1)'};

  ${Logo}:hover & {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    max-width: 140px;
  }
`;

export const BrandDescription = styled.p`
  font-size: 15px;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.7;
  line-height: 1.7;
  margin-bottom: 24px;
  max-width: 320px;
  font-family: ${fonts.body};
`;

export const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SocialLink = styled(motion.a)`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${(props) =>
    props.$isHomePage ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid
    ${(props) =>
      props.$isHomePage ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.7;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 18px;

  &:hover {
    background: ${colors.sapphire};
    border-color: ${colors.sapphire};
    color: white;
    opacity: 1;
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(28, 80, 196, 0.2);
  }
`;

export const Column = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

export const ColumnTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 32px;
  color: ${(props) =>
    props.$isHomePage ? colors.frenchBlue : colors.porcelain};
  font-family: ${fonts.headline};
  opacity: 0.9;
`;

export const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const LinkItem = styled.li`
  margin-bottom: 14px;
`;

export const FooterLink = styled(Link)`
  font-size: 15px;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.65;
  text-decoration: none;
  transition: all 0.3s ease;
  font-family: ${fonts.body};
  display: inline-block;

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
    transform: translateX(4px);
  }
`;

export const InfoText = styled.div`
  font-size: 15px;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.8;
  line-height: 1.6;
  font-family: ${fonts.body};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ContactLink = styled.div`
  color: inherit;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    opacity: 0.5;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  strong {
    font-weight: 500;
    opacity: 0.9;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-block;
    width: fit-content;

    &:hover {
      color: ${colors.sapphire};
      opacity: 1;
    }
  }
`;

export const MapContainer = styled.div`
  width: 100%;
  height: 240px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  background: ${(props) =>
    props.$isHomePage ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.$isHomePage ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    filter: ${(props) =>
      props.$isHomePage ? 'none' : 'grayscale(1) invert(0.9) opacity(0.8)'};
  }
`;

export const BottomSection = styled.div`
  padding-top: 48px;
  border-top: 1px solid
    ${(props) =>
      props.$isHomePage ? 'rgba(0, 0, 0, 0.1)' : 'rgba(240, 244, 239, 0.1)'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const Copyright = styled(motion.div)`
  font-size: 14px;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.6;
  font-family: ${fonts.body};
`;

export const LegalLinks = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 24px;
  }
`;

export const LegalLink = styled(Link)`
  font-size: 14px;
  color: ${(props) =>
    props.$isHomePage ? colors.deepCharcoal : colors.porcelain};
  opacity: 0.6;
  text-decoration: none;
  transition: all 0.3s ease;
  font-family: ${fonts.body};

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
  }
`;
